/**
 * useFormShell — the ONE hook a new form calls to wire up the standard shell:
 * notifications, the shared save / delete flows, the confirm/alert/prompt
 * provider, the FormTitle props (sourced from navMeta so the heading can never
 * drift from the sidebar), and the date helpers.  One hook instead of importing
 * and wiring five separate pieces.
 *
 *   function MyForm() {
 *     const shell = useFormShell('sec-roles');
 *     // …
 *     return (
 *       <>
 *         <FormTitle {...shell.titleProps} loadMs={loadMs} loading={loading} auditFormName="Roles" />
 *         <AppToolbar onNew={openNew} onDelete={() => shell.runDelete({ … })} … />
 *         {/* shell.runSave / shell.notify / shell.confirm / shell.fmt.dateTime … *\/}
 *       </>
 *     );
 *   }
 *
 * Everything it returns is the EXISTING shared implementation — this hook only
 * composes them, so behaviour is identical to wiring them by hand.
 */
import { useNotify } from '../components/notifications';
import { useSaveFlow, useDeleteFlow, useConfirm } from '../components/confirm';
import { navMeta } from '../layouts/navData';
import { fmtLocalDate, fmtLocalDateTime, fmtLocalTime } from '../utils/datetime';

/** The nav-derived title props, ready to spread into <FormTitle>. */
export interface FormTitleProps {
  pageId: string;
  title?: string;
  icon?: string;
  iconColor?: string;
}

export interface FormShell {
  /** Shared notification banner (§13). */
  notify: ReturnType<typeof useNotify>;
  /** Shared save flow (confirm → audited handler → "Saved successfully."). */
  runSave: ReturnType<typeof useSaveFlow>;
  /** Shared delete flow (danger confirm → reason gate → "Deleted."). */
  runDelete: ReturnType<typeof useDeleteFlow>;
  /** Shared confirm / alert / prompt provider. */
  confirm: ReturnType<typeof useConfirm>;
  /** Spread into <FormTitle> — icon/colour/label come from navMeta(pageId). */
  titleProps: FormTitleProps;
  /** The canonical date helpers (§14 — never format dates inline). */
  fmt: {
    date: typeof fmtLocalDate;
    dateTime: typeof fmtLocalDateTime;
    time: typeof fmtLocalTime;
  };
}

/**
 * @param pageId  The nav id (same one <FormTitle pageId> uses) — drives the
 *                title icon / colour / label from navMeta.
 */
export function useFormShell(pageId: string): FormShell {
  const notify = useNotify();
  const runSave = useSaveFlow();
  const runDelete = useDeleteFlow();
  const confirm = useConfirm();

  const meta = navMeta(pageId);
  const titleProps: FormTitleProps = {
    pageId,
    title: meta?.label,
    icon: meta?.icon,
    iconColor: meta?.iconColor,
  };

  return {
    notify,
    runSave,
    runDelete,
    confirm,
    titleProps,
    fmt: { date: fmtLocalDate, dateTime: fmtLocalDateTime, time: fmtLocalTime },
  };
}
